"use client";

import { useEffect, useRef, useState } from "react";
import { Pointer } from "lucide-react";
import { useLang } from "@/lib/LanguageContext";

/** Space left above the next section for the floating header. */
const HEADER_OFFSET = 96;
/** Scroll distance (px) after which the hint fades away. */
const HIDE_AFTER = 40;

/**
 * Bottom-centre "scroll down" cue for hero sections — a mouse on desktop, a
 * swiping hand on touch screens. Clicking it glides to the end of its section.
 * Place it as a direct child of the (relative) hero <section>.
 */
export function ScrollHint({ className = "" }: { className?: string }) {
  const { t } = useLang();
  const ref = useRef<HTMLButtonElement>(null);
  const [hidden, setHidden] = useState(false);

  // Fade out as soon as the visitor starts scrolling; come back at the top.
  useEffect(() => {
    const onScroll = () => setHidden(window.scrollY > HIDE_AFTER);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollDown = () => {
    const section = ref.current?.closest("section");
    const target = section
      ? section.getBoundingClientRect().bottom + window.scrollY - HEADER_OFFSET
      : window.scrollY + window.innerHeight * 0.8;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: Math.max(target, window.scrollY + 1), behavior: reduceMotion ? "auto" : "smooth" });
  };

  return (
    <button
      ref={ref}
      type="button"
      onClick={scrollDown}
      aria-label={t.scrollHint}
      aria-hidden={hidden}
      tabIndex={hidden ? -1 : 0}
      className={`scroll-hint group absolute bottom-3 left-1/2 z-20 -translate-x-1/2 cursor-pointer lg:bottom-8 ${hidden ? "pointer-events-none" : ""} ${className}`}
    >
      {/* Opacity lives on this wrapper — the button's own opacity is held by the fade-in animation */}
      <span
        className={`flex items-center justify-center transition-all duration-500 ease-out ${
          hidden ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
        }`}
      >
      {/* Desktop — mouse with a rolling wheel */}
      <span className="hidden h-10 w-6 justify-center rounded-full border-2 border-white/35 pt-2 transition-colors duration-300 group-hover:border-white/70 md:flex">
        <span className="scroll-hint-wheel block h-2 w-1 rounded-full bg-gradient-to-b from-[#FFB76C] to-[#E040A0]" />
      </span>

      {/* Mobile — hand swiping up */}
      <span className="flex h-11 w-11 items-center justify-center text-white/80 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] md:hidden">
        <Pointer size={26} strokeWidth={1.5} className="scroll-hint-hand" />
      </span>
      </span>
    </button>
  );
}
